import { useNavigate } from 'react-router-dom';
import { HeartPulse, Cpu, TrendingUp, BookOpen, Users, Wrench, Utensils, Palette, ArrowRight } from 'lucide-react';

const PATHWAYS = [
  { icon: HeartPulse, name: 'Healthcare', body: 'Nursing, PSW, medical office admin, allied health.', slug: 'healthcare' },
  { icon: Cpu, name: 'Technology', body: 'Software, data, cybersecurity, network admin.', slug: 'technology' },
  { icon: TrendingUp, name: 'Business', body: 'Accounting, marketing, HR, project management.', slug: 'business' },
  { icon: BookOpen, name: 'Education', body: 'Early childhood, teaching assistant, adult ed.', slug: 'education' },
  { icon: Users, name: 'Community Services', body: 'Social work, addictions, community support.', slug: 'community' },
  { icon: Wrench, name: 'Trades', body: 'Electrician, plumbing, welding, auto, HVAC.', slug: 'trades' },
  { icon: Utensils, name: 'Hospitality', body: 'Culinary arts, hotel management, tourism.', slug: 'hospitality' },
  { icon: Palette, name: 'Creative Industries', body: 'Graphic design, video, digital media, UX.', slug: 'creative' },
];

export default function PathwaysPage() {
  const navigate = useNavigate();
  return (
    <div className="bg-brand-alabaster font-sans-clean text-brand-obsidian" data-testid="pathways-page">
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-24 pb-16">
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-pine font-bold mb-4">Career Pathways</div>
        <h1 className="font-serif-editorial text-5xl md:text-6xl leading-[1.05] tracking-tight">
          Pick a direction. <span className="text-brand-pine italic">We&apos;ll map the route.</span>
        </h1>
        <p className="mt-8 text-lg text-brand-slate leading-relaxed max-w-2xl">
          We organise our work around outcomes, not institutions. Each pathway links to programs, expected
          career opportunities, and typical timelines — so you can see the finish line before you start.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-border/40 border border-brand-border/40 rounded-3xl overflow-hidden">
          {PATHWAYS.map((p) => (
            <button
              key={p.slug}
              id={p.slug}
              onClick={() => navigate(`/programs?category=${p.slug}`)}
              className="bg-white p-10 text-left group hover:bg-brand-pine hover:text-white transition-colors"
              data-testid={`pathway-hub-${p.slug}`}
            >
              <p.icon size={30} className="text-brand-coral" />
              <h3 className="font-serif-editorial text-2xl mt-6 leading-snug">{p.name}</h3>
              <p className="text-sm text-brand-slate group-hover:text-white/70 mt-3 leading-relaxed max-w-xs">{p.body}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">
                Explore programs <ArrowRight size={14} />
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-brand-pine text-white">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-serif-editorial text-3xl md:text-5xl leading-tight">Not sure which pathway fits you?</h2>
          <button
            onClick={() => navigate('/contact')}
            data-testid="pathways-cta"
            className="mt-8 inline-flex items-center gap-2 bg-brand-coral hover:bg-brand-coral-deep text-white text-sm font-semibold px-8 py-4 rounded-full transition-colors"
          >
            Book a Free Consultation <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
}
